import { type Container, Graphics } from "pixi.js"
import { GRAPH_CONTAINER_NAME } from "@/domains/visualization/constants/graph"
import type { PositionedEdge } from "@/domains/visualization/types/Graph"
import { easeInOut } from "@/domains/visualization/utils/easeInOut/easeInOut"

interface Point {
  x: number
  y: number
}

export interface EdgeSection {
  startPoint: Point
  endPoint: Point
  bendPoints: Point[]
}

export interface AnimationState {
  startTime: number
  oldSection: EdgeSection
  newSection: EdgeSection
  edgeInstance: Graphics
  isNewEdge: boolean
}

export type AnimationMap = Map<string, AnimationState>

/**
 * Find an edge instance by ID in the stage
 */
const findEdgeInstance = (stage: Container, edgeId: string): Graphics | null => {
  const container = stage.getChildByName(GRAPH_CONTAINER_NAME)
  if (!container) return null

  const edge = container.children?.find((child) => "name" in child && child.name === edgeId)

  return edge instanceof Graphics ? edge : null
}

/**
 * Create animation state for an edge
 */
export const createAnimationState = (
  oldSection: EdgeSection,
  newSection: EdgeSection,
  edgeInstance: Graphics,
  isNewEdge: boolean
): AnimationState => ({
  startTime: performance.now(),
  oldSection,
  newSection,
  edgeInstance,
  isNewEdge
})

/**
 * Initialize animation state for a changed edge
 */
export const initializeChangedEdgeAnimation = (
  edge: PositionedEdge,
  previousEdge: PositionedEdge,
  stage: Container
): AnimationState | null => {
  const edgeInstance = findEdgeInstance(stage, edge.id)
  if (!edgeInstance || !previousEdge.sections[0] || !edge.sections[0]) return null

  return createAnimationState(previousEdge.sections[0], edge.sections[0], edgeInstance, false)
}

/**
 * Initialize animation state for a new edge
 */
export const initializeNewEdgeAnimation = (
  edge: PositionedEdge,
  sourcePosition: Point,
  stage: Container
): AnimationState | null => {
  const edgeInstance = findEdgeInstance(stage, edge.id)
  if (!edgeInstance || !edge.sections[0]) return null

  const startSection = {
    startPoint: sourcePosition,
    endPoint: sourcePosition,
    bendPoints: []
  }

  return createAnimationState(startSection, edge.sections[0], edgeInstance, true)
}

/**
 * Update edge graphics based on animation progress
 */
export const updateEdgePosition = (animation: AnimationState, currentTime: number, duration: number): boolean => {
  const elapsed = currentTime - animation.startTime
  const progress = Math.min(elapsed / duration, 1)
  const easedProgress = easeInOut(progress)

  const { edgeInstance, oldSection, newSection, isNewEdge } = animation

  // Check if edge instance is still valid before attempting to draw
  if (!edgeInstance || edgeInstance.destroyed) {
    return true // Mark animation as completed to clean it up
  }

  // Clear previous drawing
  edgeInstance.clear()
  edgeInstance.lineStyle({ width: 1, color: 0x000000, alpha: 1, native: true })

  if (isNewEdge) {
    // For new edges, grow from start to end
    const allPoints = [newSection.startPoint, ...newSection.bendPoints, newSection.endPoint]
    const totalPoints = allPoints.length
    const progressPoints = Math.ceil(totalPoints * easedProgress)

    // Draw the visible portion of the edge
    edgeInstance.moveTo(allPoints[0].x, allPoints[0].y)
    for (let i = 1; i < progressPoints && i < totalPoints; i++) {
      edgeInstance.lineTo(allPoints[i].x, allPoints[i].y)
    }
  } else {
    // For existing edges, interpolate all points
    const startPoint = {
      x: oldSection.startPoint.x + (newSection.startPoint.x - oldSection.startPoint.x) * easedProgress,
      y: oldSection.startPoint.y + (newSection.startPoint.y - oldSection.startPoint.y) * easedProgress
    }

    const endPoint = {
      x: oldSection.endPoint.x + (newSection.endPoint.x - oldSection.endPoint.x) * easedProgress,
      y: oldSection.endPoint.y + (newSection.endPoint.y - oldSection.endPoint.y) * easedProgress
    }

    // Interpolate bendpoints if they exist in both sections
    const bendPoints = oldSection.bendPoints.map((oldPoint, index) => {
      const newPoint = newSection.bendPoints[index]

      return {
        x: oldPoint.x + (newPoint.x - oldPoint.x) * easedProgress,
        y: oldPoint.y + (newPoint.y - oldPoint.y) * easedProgress
      }
    })

    // Draw the interpolated edge
    edgeInstance.moveTo(startPoint.x, startPoint.y)

    for (const point of bendPoints) {
      edgeInstance.lineTo(point.x, point.y)
    }

    edgeInstance.lineTo(endPoint.x, endPoint.y)
  }

  return progress >= 1
}

/**
 * Compare two edge sections to detect changes
 */
const haveSectionsChanged = (currentSection: EdgeSection, previousSection: EdgeSection): boolean => {
  const pointsChanged =
    currentSection.startPoint.x !== previousSection.startPoint.x ||
    currentSection.startPoint.y !== previousSection.startPoint.y ||
    currentSection.endPoint.x !== previousSection.endPoint.x ||
    currentSection.endPoint.y !== previousSection.endPoint.y

  const bendPointsChanged =
    currentSection.bendPoints.length !== previousSection.bendPoints.length ||
    currentSection.bendPoints.some((point, j) => {
      const prevPoint = previousSection.bendPoints[j]
      return point.x !== prevPoint.x || point.y !== prevPoint.y
    })

  return pointsChanged || bendPointsChanged
}

/**
 * Detect which edges have changed or are new
 */
export const detectEdgeChanges = (currentEdges: PositionedEdge[], previousEdges: PositionedEdge[]) => {
  const previousEdgesMap = new Map(previousEdges.map((edge) => [edge.id, edge]))

  return currentEdges.reduce(
    (acc, currentEdge) => {
      const previousEdge = previousEdgesMap.get(currentEdge.id)

      if (!previousEdge) {
        acc.newEdges.push(currentEdge)
        return acc
      }

      if (currentEdge.sections.some((section, i) => haveSectionsChanged(section, previousEdge.sections[i]))) {
        acc.changedEdges.push(currentEdge)
      }

      return acc
    },
    { changedEdges: [] as PositionedEdge[], newEdges: [] as PositionedEdge[] }
  )
}
